import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Gem, MessageCircle, Eye, Coins, Crown,
  Play, Pause, ChevronRight, Heart, Share2, Volume2, VolumeX
} from 'lucide-react';
import { voiceVolume, getStoredAudioMode } from '../../utils/modeAudio';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ──────────────────────────────────────────────────────────────────
 * NetworkerAffirmationsPage — restyled to match THE EDGE dark amber
 * aesthetic. Adds spoken playback via the Web Speech API
 * (browser-native, no key, calm slower rate).
 * ──────────────────────────────────────────────────────────────── */

const ICON_MAP = {
  gem: Gem,
  'message-circle': MessageCircle,
  eye: Eye,
  coins: Coins,
  crown: Crown,
};

// Try to pick a calm, low, native-English voice if the browser exposes one.
function pickPreferredVoice() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices?.length) return null;
  const ranked = ["Daniel", "Google UK English Male", "Microsoft Ryan", "Alex", "Google UK English Female", "Samantha"];
  for (const name of ranked) {
    const hit = voices.find((v) => v.name === name);
    if (hit) return hit;
  }
  return voices.find((v) => v.lang?.startsWith('en-GB')) ||
         voices.find((v) => v.lang?.startsWith('en')) ||
         voices[0];
}

function speak(text, voice, onEnd) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) { onEnd?.(); return null; }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.rate = 0.86;
    u.pitch = 0.95;
    // Respect Clear / Soft / Deep globally.
    u.volume = voiceVolume(getStoredAudioMode());
    u.onend = () => onEnd?.();
    u.onerror = () => onEnd?.();
    synth.speak(u);
    return u;
  } catch {
    onEnd?.();
    return null;
  }
}

const NetworkerAffirmationsPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const voiceRef = useRef(null);
  const advanceTimerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('reclaim_token');
    fetch(`${API_URL}/api/networker/affirmations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setCategories(d.categories || []))
      .catch((err) => console.error('Affirmations fetch failed', err))
      .finally(() => setLoading(false));

    // Voices load async — wait for them before we lock in our pick.
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      const setIt = () => { voiceRef.current = pickPreferredVoice(); };
      setIt();
      window.speechSynthesis.onvoiceschanged = setIt;
    }
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Keep speech in sync with the active affirmation while playing.
  useEffect(() => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (!isPlaying || !selectedCategory) return;
    const text = selectedCategory.affirmations[currentIndex];
    if (!text) return;

    const advance = () => {
      setCurrentIndex((i) => {
        if (i >= selectedCategory.affirmations.length - 1) {
          setIsPlaying(false);
          return 0;
        }
        return i + 1;
      });
    };

    if (voiceOn && typeof window !== 'undefined' && window.speechSynthesis) {
      // Speak first; on end, hold ~700ms then advance.
      speak(text, voiceRef.current, () => {
        advanceTimerRef.current = setTimeout(advance, 700);
      });
    } else {
      // No speech — just hold each affirmation 5s.
      advanceTimerRef.current = setTimeout(advance, 5000);
    }

    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isPlaying, currentIndex, selectedCategory, voiceOn]);

  const handleSelect = (cat) => {
    setSelectedCategory(cat);
    setCurrentIndex(0);
    setIsPlaying(false);
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  const togglePlayback = () => setIsPlaying((p) => !p);
  const next = () => {
    if (!selectedCategory) return;
    setCurrentIndex((i) => Math.min(selectedCategory.affirmations.length - 1, i + 1));
  };
  const prev = () => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'radial-gradient(ellipse at 50% 30%, #1A1410 0%, #0E0B08 55%, #050402 100%)' }}
      >
        <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // ── Category list ────────────────────────────────────────────────
  if (!selectedCategory) {
    return (
      <div
        className="min-h-screen"
        style={{ background: 'linear-gradient(180deg, #050402 0%, #070605 40%, #050402 100%)' }}
        data-testid="networker-affirmations-list"
      >
        <div className="px-6 pt-12 pb-6">
          <button
            onClick={() => navigate('/networker')}
            className="flex items-center gap-2 text-white/55 hover:text-white transition-colors mb-6"
            data-testid="affirmations-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-[11px] uppercase tracking-[0.3em]">The Edge</span>
          </button>
          <p className="text-[10px] uppercase tracking-[0.5em] text-amber-500/85 mb-2">Affirmations</p>
          <h1 className="text-2xl font-semibold text-white mb-1.5" style={{ letterSpacing: '-0.012em' }}>
            Rewire the identity
          </h1>
          <p className="text-white/55 text-sm">Choose the layer you're installing today.</p>
        </div>

        <div className="px-6 space-y-2.5 pb-24">
          {categories.map((category) => {
            const Icon = ICON_MAP[category.icon] || Gem;
            return (
              <button
                key={category.id}
                onClick={() => handleSelect(category)}
                data-testid={`affirmations-cat-${category.id}`}
                className="w-full text-left rounded-xl px-4 py-3.5 flex items-center gap-4 transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, rgba(15,11,8,0.92), rgba(8,6,4,0.92))',
                  border: '1px solid rgba(217,165,90,0.14)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(217,165,90,0.22), rgba(217,165,90,0.05))',
                    boxShadow: '0 6px 18px -10px rgba(217,165,90,0.55)',
                  }}
                >
                  <Icon className="w-5 h-5 text-amber-200/95" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white text-[15px] font-semibold leading-tight" style={{ letterSpacing: '-0.005em' }}>
                    {category.name}
                  </h3>
                  <p className="text-white/45 text-xs mt-0.5">{category.description}</p>
                </div>
                <div className="flex items-center gap-2 text-white/30">
                  <span className="text-[11px]">{category.affirmations?.length || 0}</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Playback view ───────────────────────────────────────────────
  const current = selectedCategory.affirmations[currentIndex];
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 50% 30%, #1A1410 0%, #0E0B08 55%, #050402 100%)' }}
      data-testid="networker-affirmations-playback"
    >
      {/* Slow drifting amber atmosphere */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 30% 25%, rgba(217,165,90,0.08) 0%, transparent 60%), radial-gradient(circle at 70% 80%, rgba(217,165,90,0.06) 0%, transparent 60%)',
        }}
      />

      <div className="relative z-10 px-6 pt-12 pb-6">
        <button
          onClick={() => setSelectedCategory(null)}
          className="flex items-center gap-2 text-white/55 hover:text-white transition-colors mb-4"
          data-testid="affirmations-back-categories-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-[11px] uppercase tracking-[0.3em]">Categories</span>
        </button>
        <p className="text-[10px] uppercase tracking-[0.4em] text-amber-500/80">{selectedCategory.name}</p>
      </div>

      {/* Phrase */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-8">
        <div className="w-full max-w-xl text-center">
          {/* Progress hairline */}
          <div className="flex gap-1 mb-8 justify-center">
            {selectedCategory.affirmations.map((_, i) => (
              <div
                key={i}
                className="h-px transition-all duration-300"
                style={{
                  width: i === currentIndex ? 28 : 8,
                  background: i <= currentIndex ? 'rgba(217,165,90,0.85)' : 'rgba(255,255,255,0.10)',
                }}
              />
            ))}
          </div>

          <p
            key={currentIndex}
            onClick={next}
            className="cursor-pointer animate-fade-in"
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: 'clamp(28px, 5.5vw, 44px)',
              color: 'rgba(255,225,180,1)',
              fontWeight: 500,
              lineHeight: 1.28,
              textShadow: '0 8px 38px rgba(217,165,90,0.42)',
              letterSpacing: '0.012em',
            }}
            data-testid="affirmations-phrase"
          >
            "{current}"
          </p>

          <p className="mt-8 text-white/35 text-[11px] uppercase tracking-[0.4em]">
            {currentIndex + 1} of {selectedCategory.affirmations.length}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="relative z-10 px-6 pb-12">
        <div className="flex items-center justify-center gap-5">
          <button
            onClick={prev}
            disabled={currentIndex === 0}
            className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-30 transition-opacity"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
            data-testid="affirmations-prev-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white/70" />
          </button>

          <button
            onClick={togglePlayback}
            data-testid="affirmations-play-btn"
            className="w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(217,165,90,0.92), rgba(180,120,60,0.95))',
              boxShadow: '0 14px 38px -14px rgba(217,165,90,0.75), inset 0 1px 0 rgba(255,255,255,0.10)',
            }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="w-7 h-7 text-[#1a1410]" />
            ) : (
              <Play className="w-7 h-7 text-[#1a1410] ml-1" />
            )}
          </button>

          <button
            onClick={next}
            disabled={currentIndex === selectedCategory.affirmations.length - 1}
            className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-30 transition-opacity"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
            data-testid="affirmations-next-btn"
          >
            <ChevronRight className="w-5 h-5 text-white/70" />
          </button>
        </div>

        <div className="flex justify-center gap-3 mt-6">
          <button
            onClick={() => setVoiceOn((v) => !v)}
            data-testid="affirmations-voice-toggle"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.3em] transition-colors"
            style={{
              background: voiceOn ? 'rgba(217,165,90,0.14)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${voiceOn ? 'rgba(217,165,90,0.32)' : 'rgba(255,255,255,0.08)'}`,
              color: voiceOn ? 'rgba(255,225,180,0.95)' : 'rgba(255,255,255,0.55)',
            }}
          >
            {voiceOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            {voiceOn ? 'Voice on' : 'Voice off'}
          </button>
          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Heart className="w-3.5 h-3.5" /> Save
          </button>
          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); filter: blur(6px); }
          to   { opacity: 1; transform: translateY(0);  filter: blur(0); }
        }
        .animate-fade-in { animation: fade-in 0.85s cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  );
};

export default NetworkerAffirmationsPage;
